import type { DateRangeValue } from '#ui/DateRangeField.js';

import React, { useEffect, useState } from 'react';

import { getFilenameFromUrl, getFileTypeIcon } from '#lib/file-utils.js';
import { useAppStates, useFilePicker, useItineraryState, useTripsState } from '#states/hooks.js';
import { CurrencyInput } from '#ui/CurrencyInput.js';
import { DateRangeField } from '#ui/DateRangeField.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FloatingInput,
  IconSizes,
  MaterialIcon,
} from '#ui/index.js';
import { SectionDivider } from '#ui/SectionDivider.js';

/**
 * Dialog for marking a non-room itinerary item as booked or updating its
 * existing booking details (confirmation number + notes + attachments).
 * Rooms go through the full hotel checkout flow, not this dialog.
 */
export function BookingDetailsDialog(): React.ReactNode {
  const itineraryState = useItineraryState();
  const tripsState = useTripsState();

  const item = itineraryState.itemForBookingDialog;
  const isOpen = itineraryState.isBookingDialogOpen && item != null;

  const isManagedByElara = item?.booking?.managedByElara;

  const [dateValue, setDateValue] = useState<DateRangeValue | null>(null);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [costAmount, setCostAmount] = useState('');
  const [costCurrency, setCostCurrency] = useState('USD');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (item) {
      setConfirmationNumber(item.booking?.confirmationNumber ?? '');
      // Prefer the explicit actualCost; fall back to the booking's priceSnapshot total so an
      // external stay recorded via priceSnapshot (rather than actualCost) pre-fills its price
      // instead of showing 0.00.
      const existingCost = item.booking?.actualCost ?? item.booking?.priceSnapshot?.totalCost;
      setCostAmount(existingCost?.amount?.toString() ?? '');
      setCostCurrency(existingCost?.currency ?? 'USD');
    }
  }, [item]);

  async function handleSubmit() {
    if (!item?.id || !tripsState.current?.id || isSubmitting) return;
    if (!dateValue?.isValid) return;
    setIsSubmitting(true);
    try {
      await Promise.all([
        itineraryState.updateItemDates(item.id, {
          startDateTime: dateValue.startDateTime,
          endDateTime: dateValue.endDateTime,
        }),
        itineraryState.updateBookingDetails(item.id, {
          // null = clear the stored value (updates deep-merge server-side; an omitted key
          // would leave the old value in place).
          confirmationNumber: confirmationNumber.trim() || null,
          actualCost:
            costAmount.trim() && !Number.isNaN(Number.parseFloat(costAmount))
              ? { amount: Number.parseFloat(costAmount), currency: costCurrency }
              : null,
        }),
      ]);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    if (!isSubmitting) itineraryState.closeBookingDialog();
  }

  const trip = tripsState.current;
  const title = item?.displayTitle ?? 'Booking details';

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <SectionDivider label="When" />
          <DateRangeField
            key={item.id}
            initialStartDateTime={item.startDateTime ?? undefined}
            initialEndDateTime={item.endDateTime ?? undefined}
            tripStartDate={trip?.startDate ?? undefined}
            tripEndDate={trip?.endDate ?? undefined}
            allowPastDates
            onChange={setDateValue}
          />

          <SectionDivider label="Booking details" />
          {!isManagedByElara && (
            <FloatingInput
              id="booking-confirmation"
              label="Confirmation number"
              value={confirmationNumber}
              onChange={(e) => setConfirmationNumber(e.target.value)}
              autoComplete="off"
            />
          )}
          <CurrencyInput
            id="booking-cost"
            amount={costAmount}
            currency={costCurrency}
            onAmountChange={setCostAmount}
            onCurrencyChange={setCostCurrency}
            disabled={isSubmitting}
          />
        </div>

        {item?.booking?.status && <AttachmentsSection tripId={tripsState.current?.id} item={item} />}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!dateValue?.isValid || isSubmitting} isBusy={isSubmitting}>
            Save booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentsSection({
  tripId,
  item,
}: {
  tripId?: string;
  item: {
    id: string;
    attachments?: Array<string | { url: string; name: string; mimeType?: string; size?: number }> | null;
  };
}) {
  const { fileUploadState } = useAppStates('fileUploadState');
  const itineraryState = useItineraryState();
  const filePicker = useFilePicker();
  const [isUploading, setIsUploading] = useState(false);

  const attachments = (item.attachments ?? []).map((a) =>
    typeof a === 'string' ? { url: a, name: getFilenameFromUrl(a) } : a,
  );

  const handleAddAttachments = async () => {
    if (!tripId) return;
    const assets = await filePicker.pick({
      accept: 'image/*,application/pdf,text/*,application/msword,application/vnd.openxmlformats-officedocument.*',
      multiple: true,
    });
    if (assets.length === 0) return;

    setIsUploading(true);
    try {
      const basePath = `trips/${tripId}/itinerary/${item.id}/attachments`;
      const results = await fileUploadState.uploadFiles(assets, basePath);
      const newAttachments = results.map((r) => ({
        url: r.url,
        name: r.asset.name,
        mimeType: r.asset.mimeType || undefined,
        size: r.asset.size || undefined,
      }));
      await itineraryState.updateBookingAttachments(item.id, [...attachments, ...newAttachments]);
    } catch {
      // Error handled by itineraryState
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async (attachment: { url: string }) => {
    await itineraryState.updateBookingAttachments(
      item.id,
      attachments.filter((a) => a.url !== attachment.url),
    );
  };

  return (
    <div className="grid gap-2">
      <SectionDivider label="Attachments" className="mb-2" />
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((attachment, i) => {
            const { iconName, className: iconClassName } = getFileTypeIcon(attachment.mimeType, attachment.name);
            return (
              <div key={i} className="flex items-center gap-2 rounded border border-muted px-2 py-1 text-sm">
                <MaterialIcon icon={iconName} size={IconSizes.sm} className={iconClassName} />
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 truncate text-primary hover:underline"
                >
                  {attachment.name}
                </a>
                <button
                  type="button"
                  onClick={() => void handleRemove(attachment)}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isUploading}
        onClick={() => void handleAddAttachments()}
      >
        {isUploading ? 'Uploading...' : 'Add attachment'}
      </Button>
    </div>
  );
}
